import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Button,
  Card,
  Header,
  Image,
  Modal,
  Grid,
} from "semantic-ui-react";
import "../../node_modules/react-vis/dist/style.css";
import "../css/home.css";
import CompanyPage from "../company/CompanyPage";
import { useMobile } from "../MobileContext";

const SectorCardView = ({ stockReports }) => {
  const isMobile = useMobile().isMobile;
  const [modalState, setModalState] = useState({
    open: false,
    modalSymbol: "",
  });
  const [packedLayout, setPackedLayout] = useState(null);
  const cardRefs = useRef({});
  
  const handleShowModal = (symbol) => {
    setModalState({ ...modalState, modalSymbol: symbol, open: true });
  };

  const handleCloseModal = () => {
    setModalState({ ...modalState, open: false });
  };

  // Group stocks by sector
  const sectorData = useMemo(() => {
    if (!stockReports || stockReports.length === 0) return [];

    const sectorMap = {};

    stockReports.forEach((stock) => {
      const sector = stock.sector || "Unassigned";
      if (!sectorMap[sector]) {
        sectorMap[sector] = [];
      }
      sectorMap[sector].push(stock);
    });

    // Convert to array and sort by count (descending)
    return Object.entries(sectorMap)
      .map(([sector, stocks]) => {
        // Sort stocks by performance (descending: best to worst)
        const sortedStocks = [...stocks].sort((a, b) => {
          const perfA = Number(a.perfYear) || 0;
          const perfB = Number(b.perfYear) || 0;
          return perfB - perfA;
        });

        // Determine if card should be half-width based on stock count
        // Cards with less than 4 stocks use half-width, 4+ stocks use full-width
        const isHalfWidth = stocks.length < 4;
        
        return {
          sector,
          stocks: sortedStocks,
          count: stocks.length,
          isHalfWidth: isHalfWidth,
        };
      })
      .sort((a, b) => b.count - a.count); // Sort sectors by stock count
  }, [stockReports]);

  // Measure card heights and pack them efficiently
  useEffect(() => {
    if (!sectorData || sectorData.length === 0) return;

    const measureAndPack = () => {
      const cardHeights = {};
      
      // Measure all card heights
      Object.keys(cardRefs.current).forEach((sector) => {
        const element = cardRefs.current[sector];
        if (element) {
          cardHeights[sector] = element.offsetHeight;
        }
      });

      // Pack cards into columns (using a simple bin packing algorithm)
      if (Object.keys(cardHeights).length === 0) {
        // If heights not measured yet, use default 3-column layout
        setPackedLayout(null);
        return;
      }

      const numColumns = isMobile ? 1 : 3;
      const gap = 16; // Gap between cards
      const columns = Array(numColumns).fill(null).map(() => ({
        items: [],
        height: 0,
        widthOccupied: 0, // Track how much width is occupied (0.5 for half-width, 1.0 for full-width)
        currentRowItems: [],
      }));

      // Sort sectors: process smaller cards first to create gaps, then fill them with larger cards
      const sortedSectors = [...sectorData].sort((a, b) => {
        const heightA = cardHeights[a.sector] || 0;
        const heightB = cardHeights[b.sector] || 0;
        // Sort by height ascending (smallest first) so smaller cards create gaps for larger ones
        return heightA - heightB;
      });

      // Track current column heights - represents the bottom of cards in each column
      let columnHeights = Array(numColumns).fill(0);

      sortedSectors.forEach((sectorGroup) => {
        const cardHeight = cardHeights[sectorGroup.sector] || 400;
        const cardWidth = sectorGroup.isHalfWidth ? 0.5 : 1.0;
        
        const minHeight = Math.min(...columnHeights);
        const maxHeight = Math.max(...columnHeights);
        
        let targetColumnIndex = -1;
        let bestScore = Infinity;
        
        // Evaluate each column to find the best placement
        for (let i = 0; i < numColumns; i++) {
          const currentHeight = columnHeights[i];
          const column = columns[i];
          
          // Check if column has space for this card width
          if (column.widthOccupied + cardWidth > 1.0) {
            continue; // Column is full, skip it
          }
          
          // Calculate score - lower is better
          let score = 0;
          
          // CRITICAL: Pair half-width cards side-by-side
          if (sectorGroup.isHalfWidth && column.widthOccupied === 0.5) {
            const lastItem = column.items.length > 0 ? column.items[column.items.length - 1] : null;
            if (lastItem && lastItem.isHalfWidth) {
              score = -100000; // Extremely strong - always pair
            }
          }
          
          // Prefer filling gaps in columns that already have content
          const gapToFill = maxHeight - currentHeight;
          if (gapToFill > 0) {
            score -= gapToFill * 200; // Strong bonus for filling gaps
          }
          
          // Penalty for empty columns when others have content
          const hasOtherContent = columnHeights.some((h, idx) => idx !== i && h > 0);
          if (currentHeight === 0 && hasOtherContent) {
            score += 2000; // Heavy penalty
          }
          
          // Prefer shortest column
          if (currentHeight === minHeight && currentHeight > 0) {
            score -= 500;
          }
          
          // For half-width cards, prefer columns with existing half-width cards
          if (sectorGroup.isHalfWidth && column.items.some(item => item.isHalfWidth)) {
            score -= 3000; // Strong grouping bonus
          }
          
          if (score < bestScore) {
            bestScore = score;
            targetColumnIndex = i;
          }
        }
        
        // If no column has space, find the column with minimum height
        if (targetColumnIndex === -1) {
          targetColumnIndex = columnHeights.indexOf(minHeight);
        }
        
        // Place card in the selected column
        const column = columns[targetColumnIndex];
        
        // Check if we need to start a new row (current row is full)
        if (column.widthOccupied + cardWidth > 1.0) {
          // Column row is full, start new row
          if (column.currentRowItems && column.currentRowItems.length > 0) {
            const rowHeight = Math.max(...column.currentRowItems.map(item => item.cardHeight || 400));
            column.height += rowHeight + gap;
            column.currentRowItems = [];
          }
          column.widthOccupied = 0;
          columnHeights[targetColumnIndex] = column.height;
        }
        
        // Track row start for grouping later
        const isRowStart = column.widthOccupied === 0;
        
        // Initialize currentRowItems if needed
        if (!column.currentRowItems) {
          column.currentRowItems = [];
        }
        
        // Add card to column
        column.items.push({
          ...sectorGroup,
          cardHeight: cardHeight,
          rowStart: isRowStart,
          cardWidth: cardWidth,
        });
        column.currentRowItems.push({
          ...sectorGroup,
          cardHeight: cardHeight,
        });
        column.widthOccupied += cardWidth;
        
        // Update column height based on current row max height
        const currentRowHeight = Math.max(...column.currentRowItems.map(item => item.cardHeight || 400));
        const completedRowsHeight = column.height;
        columnHeights[targetColumnIndex] = completedRowsHeight + currentRowHeight;
        
        // If row is complete (width >= 1.0), finalize this row
        if (column.widthOccupied >= 1.0) {
          column.height += currentRowHeight + gap;
          column.currentRowItems = [];
          column.widthOccupied = 0; // Reset for next row
          columnHeights[targetColumnIndex] = column.height;
        }
      });

      // Group items into rows within each column for rendering
      const columnsWithRows = columns.map(column => {
        const rows = [];
        let currentRow = [];
        let currentRowWidth = 0;
        
        column.items.forEach((item) => {
          const itemWidth = item.isHalfWidth ? 0.5 : 1.0;
          
          // If adding this item would exceed row width, start a new row
          if (currentRowWidth + itemWidth > 1.0 && currentRow.length > 0) {
            rows.push(currentRow);
            currentRow = [item];
            currentRowWidth = itemWidth;
          } else {
            currentRow.push(item);
            currentRowWidth += itemWidth;
          }
          
          // If row is complete (width >= 1.0), finish it and start new row
          if (currentRowWidth >= 1.0) {
            rows.push(currentRow);
            currentRow = [];
            currentRowWidth = 0;
          }
        });
        
        // Add remaining items as last row
        if (currentRow.length > 0) {
          rows.push(currentRow);
        }
        
        return { ...column, rows };
      });

      setPackedLayout(columnsWithRows);
    };

    // Use a small delay to ensure DOM is rendered
    const timer = setTimeout(measureAndPack, 100);
    return () => clearTimeout(timer);
  }, [sectorData, isMobile]);

  if (!stockReports || stockReports.length === 0) {
    return (
      <div
        style={{
          padding: "2rem",
          textAlign: "center",
          color: "#666",
        }}
      >
        No stock data available
      </div>
    );
  }

  // Color palette for sectors
  const sectorColors = [
    "#3b82f6", // blue
    "#10b981", // green
    "#f59e0b", // amber
    "#ef4444", // red
    "#8b5cf6", // purple
    "#ec4899", // pink
    "#06b6d4", // cyan
    "#84cc16", // lime
    "#f97316", // orange
    "#6366f1", // indigo
    "#14b8a6", // teal
    "#a855f7", // violet
    "#eab308", // yellow
    "#22c55e", // emerald
    "#f43f5e", // rose
  ];

  // Get color for sector
  const getSectorColor = (index) => {
    return sectorColors[index % sectorColors.length];
  };

  // Render stock logos component (reusable, similar to PerformanceView)
  const renderStockLogos = (stocks, color, isHalfWidth = false) => {
    const gap = isMobile ? "0.35rem" : "0.4rem";
    const stockCount = stocks.length;
    
    // Determine number of columns based on stock count, card width, and mobile
    // On mobile, use fewer columns to prevent cramping
    let numColumns;
    if (isMobile) {
      // Mobile: significantly reduce columns to prevent overlap and ensure visibility
      if (stockCount <= 1) {
        numColumns = 1; // 1 stock: 1 column
      } else if (stockCount <= 3) {
        numColumns = 2; // 2-3 stocks: 2 columns
      } else if (stockCount <= 6) {
        numColumns = 3; // 4-6 stocks: 3 columns
      } else {
        numColumns = 4; // 7+ stocks: 4 columns max on mobile
      }
    } else {
      // Desktop: original logic
      if (stockCount <= 2) {
        numColumns = 2; // 1-2 stocks (half-width): 2 columns (centered)
      } else if (stockCount === 3) {
        numColumns = 3; // 3 stocks (half-width): 3 columns (all in one row)
      } else if (stockCount === 4) {
        numColumns = 5; // 4 stocks (full-width): 5 columns (all 4 fit in one row with spacing)
      } else if (stockCount <= 6) {
        numColumns = 6; // 5-6 stocks (full-width): 6 columns
      } else {
        numColumns = 7; // 7+ stocks (full-width): 7 columns (original layout)
      }
    }
    
    const totalGapWidth = isMobile 
      ? `${(numColumns - 1) * 0.5}rem` 
      : `${(numColumns - 1) * 0.4}rem`;
    const itemWidth = `calc((100% - ${totalGapWidth}) / ${numColumns})`;
    
    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: gap,
          alignItems: "flex-start",
          justifyContent: stockCount <= 2 ? "center" : "flex-start", // Center for 1-2 stocks
          width: "100%",
          overflow: "visible",
          boxSizing: "border-box",
        }}
      >
        {stocks.map((stock) => (
          <div
            key={stock.symbol}
            style={{
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "center",
              gap: isMobile ? "3px" : "4px",
              width: itemWidth,
              minWidth: itemWidth,
              maxWidth: itemWidth,
              flexShrink: 0,
              flexBasis: itemWidth,
              boxSizing: "border-box",
            }}
          >
            <div
              onClick={() => handleShowModal(stock.symbol)}
              style={{
                cursor: "pointer",
                transition: "all 0.2s ease",
                padding: "4px",
                background: "linear-gradient(145deg, #6b7280, #4b5563)",
                borderRadius: "6px",
                border: "1px solid rgba(255,255,255,0.1)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 6px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.1)",
                width: isMobile ? "40px" : "48px",
                height: isMobile ? "40px" : "48px",
                position: "relative",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px) scale(1.05)";
                e.currentTarget.style.boxShadow = `0 4px 12px ${color}50, inset 0 1px 0 rgba(255,255,255,0.15)`;
                e.currentTarget.style.borderColor = color;
                e.currentTarget.style.background = "linear-gradient(145deg, #7b8280, #5b6563)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.1)";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
                e.currentTarget.style.background = "linear-gradient(145deg, #6b7280, #4b5563)";
              }}
            >
              {stock.image ? (
                <Image
                  src={stock.image}
                  size="mini"
                  rounded
                  style={{
                    margin: "0",
                    width: isMobile ? "32px" : "40px",
                    height: isMobile ? "32px" : "40px",
                    objectFit: "contain",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                    const fallback = e.target.parentElement.querySelector(".logo-fallback");
                    if (fallback) fallback.style.display = "flex";
                  }}
                />
              ) : null}
              <span
                className="logo-fallback"
                style={{
                  display: stock.image ? "none" : "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: isMobile ? "32px" : "40px",
                  height: isMobile ? "32px" : "40px",
                  color: "white",
                  fontSize: isMobile ? "0.45rem" : "0.55rem",
                  fontWeight: "600",
                  textAlign: "center",
                }}
              >
                {stock.symbol}
              </span>
            </div>
            <span
              style={{
                fontSize: isMobile ? "0.75rem" : "0.9rem",
                color: "#333",
                fontWeight: "600",
                textAlign: "center",
                lineHeight: 1.1,
                letterSpacing: "0.1px",
              }}
            >
              {stock.symbol}
            </span>
            <span
              style={{
                fontSize: isMobile ? "0.7rem" : "0.8rem",
                color: (() => {
                  const perfYear = Number(stock.perfYear) || 0;
                  return perfYear >= 0 ? "#21ba45" : "#db2828";
                })(),
                fontWeight: "600",
                textAlign: "center",
                lineHeight: 1.1,
              }}
            >
              {(() => {
                const perfYear = Number(stock.perfYear);
                if (isNaN(perfYear)) return "N/A";
                return `${perfYear >= 0 ? "+" : ""}${perfYear.toFixed(1)}%`;
              })()}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      style={{
        minHeight: "74vh",
        padding: "1rem",
        marginBottom: "56px",
        background: "linear-gradient(135deg, #f5f7fa 0%, #e8ecf0 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        maxWidth: isMobile ? "100vw" : "none",
        overflowX: "hidden",
      }}
    >
      <Header
        as="h5"
        textAlign="left"
        style={{
          marginBottom: "1rem",
          color: "#6b7280",
          fontWeight: "500",
          fontSize: isMobile ? "0.85rem" : "0.9rem",
          width: "100%",
          maxWidth: isMobile ? "90%" : "95%",
          paddingLeft: "0.5rem",
        }}
      >
        Stocks grouped by Sector
      </Header>

      {/* Hidden measurement pass - cards rendered to measure heights */}
      <div style={{ position: "absolute", visibility: "hidden", height: 0, overflow: "hidden" }}>
        <Grid stackable columns={isMobile ? 1 : 3} style={{ width: "100%", maxWidth: "1600px" }}>
          {sectorData.map((sectorGroup, index) => {
            const color = getSectorColor(index);
            const lighterColor = `${color}20`;
            return (
              <Grid.Column key={`measure-${sectorGroup.sector}`} style={{ padding: "0.25rem" }}>
                <Card
                  ref={(el) => {
                    if (el) cardRefs.current[sectorGroup.sector] = el;
                  }}
                  style={{
                    borderRadius: "12px",
                    overflow: "hidden",
                    background: "#ffffff",
                    border: "1px solid #e5e7eb",
                    width: "100%",
                  }}
                >
                  <Card.Content style={{ padding: isMobile ? "0.6rem" : "1rem" }}>
                      <Header
                        as="h5"
                        style={{
                          margin: "0 0 1rem 0",
                          color: color,
                          fontWeight: "700",
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          background: lighterColor,
                          padding: isMobile ? "0.4rem 0.5rem" : "0.5rem 0.75rem",
                          borderRadius: "6px",
                          display: "flex",
                          alignItems: "center",
                          whiteSpace: isMobile ? "normal" : "nowrap",
                          flexWrap: "wrap",
                          gap: "0.25rem",
                        }}
                      >
                        <span>
                          {sectorGroup.sector}
                          {sectorGroup.count > 0 && (
                            <span style={{ fontSize: "0.75rem", fontWeight: "500", opacity: 0.8, marginLeft: "0.5rem" }}>
                              ({sectorGroup.count} {sectorGroup.count === 1 ? "stock" : "stocks"})
                            </span>
                          )}
                        </span>
                      </Header>
                    {sectorGroup.stocks.length > 0 ? (
                      renderStockLogos(sectorGroup.stocks, color, sectorGroup.isHalfWidth)
                    ) : (
                      <span>No stocks</span>
                    )}
                  </Card.Content>
                </Card>
              </Grid.Column>
            );
          })}
        </Grid>
      </div>

      {/* Desktop packed layout or mobile/fallback simple grid */}
      {!isMobile && packedLayout ? (
        // DESKTOP PACKED LAYOUT - sophisticated column distribution
        <div style={{ display: "flex", flexDirection: "row", gap: "0.5rem", width: "100%", maxWidth: "1600px", margin: "0 auto", boxSizing: "border-box" }}>
          {packedLayout.map((column, colIndex) => {
            if (colIndex > 2) return null;
            return (
              <div key={colIndex} style={{ flex: 1, display: "flex", flexDirection: "column", gap: "1rem" }}>
                {column.rows && column.rows.map((row, rowIndex) => (
                  <div key={rowIndex} style={{ display: "flex", flexDirection: "row", gap: "1rem", width: "100%", alignItems: "flex-start" }}>
                    {row.map((sectorGroup) => {
                      const originalSector = sectorData.find((s) => s.sector === sectorGroup.sector) || sectorGroup;
                      const originalIndex = sectorData.findIndex((s) => s.sector === sectorGroup.sector);
                      const color = getSectorColor(originalIndex >= 0 ? originalIndex : 0);
                      const lighterColor = `${color}20`;
                      
                      return (
                        <Card key={originalSector.sector} style={{ borderRadius: "12px", overflow: "hidden", boxShadow: "0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)", background: "#ffffff", border: "1px solid #e5e7eb", margin: 0, width: !originalSector.isHalfWidth ? "100%" : "calc(50% - 0.5rem)", transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)", flexShrink: 0, boxSizing: "border-box" }} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 6px 12px ${color}40, 0 4px 6px rgba(0,0,0,0.1)`; e.currentTarget.style.transform = "translateY(-2px)"; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)"; e.currentTarget.style.transform = "translateY(0)"; }}>
                          <Card.Content style={{ padding: "1rem" }}>
                            <Header as="h5" style={{ margin: "0 0 1rem 0", color: color, fontWeight: "700", fontSize: "1rem", background: lighterColor, padding: "0.5rem 0.75rem", borderRadius: "6px", display: "flex", alignItems: "center", whiteSpace: "nowrap" }}>
                              <span>{originalSector.sector} {originalSector.count > 0 && <span style={{ fontSize: "0.75rem", fontWeight: "500", opacity: 0.8, marginLeft: "0.5rem" }}>({originalSector.count} {originalSector.count === 1 ? "stock" : "stocks"})</span>}</span>
                            </Header>
                            {originalSector.stocks && originalSector.stocks.length > 0 ? renderStockLogos(originalSector.stocks, color, originalSector.isHalfWidth) : <span style={{ color: "#999", fontSize: "0.65rem", fontStyle: "italic", display: "block", textAlign: "center", padding: "1rem" }}>No stocks</span>}
                          </Card.Content>
                        </Card>
                      );
                    })}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        // MOBILE (stackable=true) or DESKTOP fallback - simple Grid automatically stacks on mobile
        <Grid 
          stackable 
          columns={3}
          style={{ 
            width: "100%", 
            maxWidth: "1600px", 
            margin: "0 auto"
          }}
        >
          {sectorData.map((sectorGroup, index) => {
            const color = getSectorColor(index);
            const lighterColor = `${color}20`;
            
            return (
              <Grid.Column key={sectorGroup.sector} style={{ paddingLeft: isMobile ? "1rem" : "0.25rem", paddingRight: isMobile ? "1rem" : "0.25rem", display: "flex", justifyContent: "center", alignItems: "stretch" }}>
                <Card
                  style={{
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)",
                    background: "#ffffff",
                    border: "1px solid #e5e7eb",
                    margin: 0,
                    width: "100%",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    height: "100%",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = `0 6px 12px ${color}40, 0 4px 6px rgba(0,0,0,0.1)`;
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <Card.Content style={{ padding: isMobile ? "0.75rem" : "1rem" }}>
                    <Header
                      as="h5"
                      style={{
                        margin: "0 0 1rem 0",
                        color: color,
                        fontWeight: "700",
                        fontSize: isMobile ? "0.9rem" : "1rem",
                        background: lighterColor,
                        padding: "0.5rem 0.75rem",
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span>
                        {sectorGroup.sector}
                        {sectorGroup.count > 0 && (
                          <span style={{ fontSize: "0.75rem", fontWeight: "500", opacity: 0.8, marginLeft: "0.5rem" }}>
                            ({sectorGroup.count} {sectorGroup.count === 1 ? "stock" : "stocks"})
                          </span>
                        )}
                      </span>
                    </Header>
                    {sectorGroup.stocks.length > 0 ? (
                      renderStockLogos(sectorGroup.stocks, color, sectorGroup.isHalfWidth)
                    ) : (
                      <span
                        style={{
                          color: "#999",
                          fontSize: isMobile ? "0.6rem" : "0.65rem",
                          fontStyle: "italic",
                          display: "block",
                          textAlign: "center",
                          padding: "1rem",
                        }}
                      >
                        No stocks
                      </span>
                    )}
                  </Card.Content>
                </Card>
              </Grid.Column>
            );
          })}
        </Grid>
      )}

      <Modal
        closeOnEscape
        centered
        open={modalState.open}
        onClose={handleCloseModal}
        size="fullscreen"
        style={{
          marginLeft: "2%",
          backgroundColor: "rgba(0,0,0,0.85)",
        }}
      >
        <Modal.Content scrolling style={{ padding: "1rem" }}>
          <Modal.Description>
            <CompanyPage symbol={modalState.modalSymbol} />
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions style={{ padding: "1rem" }}>
          <Button primary onClick={handleCloseModal} style={buttonStyle}>
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

const buttonStyle = {
  backgroundColor: "#3498db",
  color: "white",
  padding: "0.6rem 1.5rem",
  borderRadius: "6px",
  border: "none",
  fontWeight: "500",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#2980b9",
    transform: "scale(1.02)",
  },
};

export default SectorCardView;
